import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { APP_CONSTANTS } from '../../constants/app.constants';
import {
  CustomerAccessToken,
  ShopifyCustomer,
} from '../../models/shopify/shopify.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly platformId = inject(PLATFORM_ID);

  private readonly accessTokenSignal = signal<string | null>(this.readStoredToken());
  private readonly expiresAtSignal = signal<string | null>(this.readStoredExpiry());
  private readonly customerSignal = signal<ShopifyCustomer | null>(null);

  readonly accessToken = this.accessTokenSignal.asReadonly();
  readonly customer = this.customerSignal.asReadonly();

  readonly isAuthenticated = computed(() => {
    const token = this.accessTokenSignal();
    const expiresAt = this.expiresAtSignal();

    if (!token || !expiresAt) {
      return false;
    }

    return new Date(expiresAt).getTime() > Date.now();
  });

  readonly displayName = computed(
    () => this.customerSignal()?.displayName ?? this.customerSignal()?.email ?? 'Guest',
  );

  persistAccessToken(token: CustomerAccessToken): void {
    this.accessTokenSignal.set(token.accessToken);
    this.expiresAtSignal.set(token.expiresAt);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(APP_CONSTANTS.storageKeys.customerAccessToken, token.accessToken);
      localStorage.setItem(APP_CONSTANTS.storageKeys.customerExpiresAt, token.expiresAt);
    }
  }

  setCustomer(customer: ShopifyCustomer | null): void {
    this.customerSignal.set(customer);
  }

  clearSession(): void {
    this.accessTokenSignal.set(null);
    this.expiresAtSignal.set(null);
    this.customerSignal.set(null);

    if (isPlatformBrowser(this.platformId)) {
      localStorage.removeItem(APP_CONSTANTS.storageKeys.customerAccessToken);
      localStorage.removeItem(APP_CONSTANTS.storageKeys.customerExpiresAt);
    }
  }

  private readStoredToken(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return localStorage.getItem(APP_CONSTANTS.storageKeys.customerAccessToken);
  }

  private readStoredExpiry(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    return localStorage.getItem(APP_CONSTANTS.storageKeys.customerExpiresAt);
  }
}
